import { EndUser, EndUserWithId } from '@app/core/interfaces/c5/end-user';
import { User } from '@app/core/interfaces/c5/user';
import { EndUserData } from '@app/core/interfaces/end-user-data';
import { AccountState } from '@app/core/interfaces/portal/account-state';
import { AppInjectorService } from '@app/core/services/app-injector.service';
import { EnhancedEndUser } from '@app/shared/models/enhanced-end-user';
import { Identifier } from '@app/utils/common';
import { isBlank } from '@app/utils/string';
import { TranslateService } from '@ngx-translate/core';
import { map } from 'rxjs/operators';

export function accountStateOf(userData: EndUserData): AccountState {
  let accountState = userData.user.state;

  const localData = userData.localData;

  if (localData !== undefined && localData.user !== undefined && localData.user.suspended === true) {
    accountState = `SUSPENDED_${userData.user.state}`;
  }

  return accountState as AccountState;
}

export function buildUserDisplayName(user: { firstName?: string; lastName: string }) {
  return [user.firstName, user.lastName].filter((f) => !isBlank(f)).join(' ');
}

export function enhanceUsersWithExtension() {
  // Enhance users with extension
  return map<EndUserWithId[], EnhancedEndUser[]>((users) => users.map(enhanceUserWithExtension));
}

type HasLoginName = { loginName?: string };
type HasExtension = { extension: string };

/**
 * Returns a copy of the user with an additional "extension" field. The
 * extension is calculated based on the login name ({extension}@{domain}).
 */
export function enhanceUserWithExtension<T extends HasLoginName>(user: T): T & HasExtension {
  return {
    ...user,
    extension: user.loginName?.split('@')[0] ?? '',
  };
}

/**
 * Returns a copy of the given user with an additional "extension" field. The field
 * contains the translated extension prefix plus the actual extension of the user.
 */
export function extendUserWithExtension<T extends User>(user: T): T & HasExtension {
  const translateService = AppInjectorService.injector.get(TranslateService);

  let extension: Identifier = user.loginName.split('@')[0];

  // isNaN accepts only *number*s, but we explicitly want to use the implicit type conversion to check if it is a valid number.
  // That way we don't need to call parseInt and hold two values (the extension as string and the parsed extension as number).
  if (isNaN(extension as any) !== undefined) {
    extension = translateService.instant('user.phone.number.extension.short') + ' ' + extension;
  }

  return {
    ...user,
    extension: String(extension),
  };
}

export function findUserExternalNumberWithInternalMain(user: EndUser, screenNumberId: Identifier) {
  const internalNr = user._mainInternalNumber.internal;

  let externalNumber;

  for (const phoneNumber of user.phoneNumbers) {
    if (phoneNumber.screenNumId === screenNumberId && phoneNumber.internal === internalNr) {
      externalNumber = phoneNumber;
    }
  }

  return externalNumber;
}

export function formatUserName(value?: { firstName?: string; lastName?: string }) {
  let returnValue = '';

  if (value !== undefined) {
    if (!isBlank(value.firstName) && !isBlank(value.lastName)) {
      returnValue = value.firstName + ' ' + value.lastName;
    } else if (!isBlank(value.lastName)) {
      returnValue = value.lastName;
    } else {
      returnValue = value.firstName;
    }
  }

  return returnValue;
}
